import type {
  Asset, CharacterStyle, FontRef, ID, Layer, Page, PageItem, Paragraph, ParagraphStyle, Snapshot, Spread, Story, TextFrameItem,
} from '@proofroom/schema';

/** Lookup tables built once per snapshot so render and edit code never scans arrays. */
export interface SnapshotIndex {
  snapshot: Snapshot;
  paraStyles: Record<ID, ParagraphStyle>;
  charStyles: Record<ID, CharacterStyle>;
  fonts: Record<ID, FontRef>;
  layers: Record<ID, Layer>;
  assets: Record<ID, Asset>;
  stories: Record<ID, Story>;
  spreads: Record<ID, Spread>;
  pages: Record<ID, Page>;
  /** Every item including group children, by id */
  items: Record<ID, PageItem>;
  frames: Record<ID, TextFrameItem>;
  /** spread index for every item id */
  itemSpread: Record<ID, number>;
  /** paragraph and its story, by paragraph id */
  paragraphs: Record<ID, { paragraph: Paragraph; storyId: ID; index: number }>;
  /** story ids that have at least one frame on each spread index */
  spreadStories: ID[][];
}

export function walkItems(items: PageItem[], visit: (item: PageItem, parents: PageItem[]) => void, parents: PageItem[] = []): void {
  for (const item of items) {
    visit(item, parents);
    if (item.kind === 'group') walkItems(item.children, visit, [...parents, item]);
  }
}

export function indexSnapshot(snapshot: Snapshot): SnapshotIndex {
  const byId = <T extends { id: ID }>(list: T[] | undefined): Record<ID, T> => {
    const out: Record<ID, T> = {};
    for (const x of list ?? []) out[x.id] = x;
    return out;
  };
  const idx: SnapshotIndex = {
    snapshot,
    paraStyles: byId(snapshot.paragraphStyles),
    charStyles: byId(snapshot.characterStyles),
    fonts: byId(snapshot.fonts),
    layers: byId(snapshot.layers),
    assets: byId(snapshot.assets),
    stories: byId(snapshot.stories),
    spreads: byId(snapshot.spreads),
    pages: {},
    items: {},
    frames: {},
    itemSpread: {},
    paragraphs: {},
    spreadStories: snapshot.spreads.map(() => []),
  };
  snapshot.spreads.forEach((spread, si) => {
    for (const page of spread.pages) idx.pages[page.id] = page;
    walkItems(spread.items, (item) => {
      idx.items[item.id] = item;
      idx.itemSpread[item.id] = si;
      if (item.kind === 'text') {
        idx.frames[item.id] = item;
        if (!idx.spreadStories[si].includes(item.storyId)) idx.spreadStories[si].push(item.storyId);
      }
    });
  });
  for (const story of snapshot.stories) {
    story.paragraphs.forEach((paragraph, index) => {
      idx.paragraphs[paragraph.id] = { paragraph, storyId: story.id, index };
    });
  }
  return idx;
}

/** Frames of a story in thread order. Falls back to threadIndex when frameIds is incomplete. */
export function storyFrames(story: Story, idx: SnapshotIndex): TextFrameItem[] {
  const listed = story.frameIds.map((id) => idx.frames[id]).filter((f): f is TextFrameItem => !!f);
  if (listed.length > 0) return listed;
  return Object.values(idx.frames)
    .filter((f) => f.storyId === story.id)
    .sort((a, b) => a.threadIndex - b.threadIndex);
}

export function spreadIndexOfPage(idx: SnapshotIndex, pageId: ID): number {
  return idx.snapshot.spreads.findIndex((s) => s.pages.some((p) => p.id === pageId));
}

/** The page a spread-space point falls on, or undefined in the gutter or outside every page. */
export function pageAtPoint(spread: Spread, point: { x: number; y: number }): Page | undefined {
  return spread.pages.find((pg) => (
    point.x >= pg.bounds.x && point.x <= pg.bounds.x + pg.bounds.w
    && point.y >= pg.bounds.y && point.y <= pg.bounds.y + pg.bounds.h
  ));
}
